import { useState, useEffect, useCallback } from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Search, Loader2, ChevronRight, X, Crown, ToggleLeft, ToggleRight, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pf = async (endpoint, options = {}) => {
  const res = await fetch(`${API_URL}/api/platform${endpoint}`, {
    ...options, credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

const PlatformCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showAssignPlan, setShowAssignPlan] = useState(false);
  const [plans, setPlans] = useState([]);
  const [assignPlanId, setAssignPlanId] = useState('');

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const data = await pf(`/companies?${params}`);
      setCompanies(data.companies);
      setTotal(data.total);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(loadCompanies, 300);
    return () => clearTimeout(t);
  }, [loadCompanies]);

  const loadDetail = async (companyId) => {
    setSelected(companyId);
    setDetailLoading(true);
    try {
      const data = await pf(`/companies/${companyId}`);
      setDetail(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleStatus = async (companyId, isActive) => {
    try {
      await pf(`/companies/${companyId}/${isActive ? 'deactivate' : 'activate'}`, { method: 'POST' });
      toast.success(`Company ${isActive ? 'deactivated' : 'activated'}`);
      loadCompanies();
      if (selected === companyId) loadDetail(companyId);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openAssignPlan = async () => {
    try {
      const p = await pf('/plans');
      setPlans(p);
      setShowAssignPlan(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAssignPlan = async () => {
    if (!assignPlanId || !selected) return;
    try {
      await pf(`/companies/${selected}/assign-plan`, {
        method: 'POST',
        body: JSON.stringify({ plan_id: assignPlanId, payment_status: 'waived' }),
      });
      toast.success('Plan assigned');
      setShowAssignPlan(false);
      setAssignPlanId('');
      loadDetail(selected);
      loadCompanies();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="platform-companies">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Companies</h1>
          <p className="text-xs text-slate-500">{total} total companies</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <Input
            placeholder="Search companies..."
            className="pl-8 h-8 text-xs bg-slate-900 border-slate-800 text-slate-300 placeholder:text-slate-600"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="platform-company-search"
          />
        </div>
        <select
          className="h-8 px-2 text-xs bg-slate-900 border border-slate-800 rounded text-slate-300"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="platform-company-status-filter"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="flex gap-4">
        {/* Table */}
        <div className={`${selected ? 'w-3/5' : 'w-full'} transition-all`}>
          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-[10px] text-slate-500 font-medium">Company</TableHead>
                    <TableHead className="text-[10px] text-slate-500 font-medium">Plan</TableHead>
                    <TableHead className="text-[10px] text-slate-500 font-medium text-center">Users</TableHead>
                    <TableHead className="text-[10px] text-slate-500 font-medium text-center">Projects</TableHead>
                    <TableHead className="text-[10px] text-slate-500 font-medium text-center">Units</TableHead>
                    <TableHead className="text-[10px] text-slate-500 font-medium">Status</TableHead>
                    <TableHead className="text-[10px] text-slate-500 font-medium w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.map(c => (
                    <TableRow
                      key={c.company_id}
                      className={`border-slate-800/50 cursor-pointer transition-colors ${
                        selected === c.company_id ? 'bg-slate-800/60' : 'hover:bg-slate-800/30'
                      }`}
                      onClick={() => loadDetail(c.company_id)}
                      data-testid={`platform-company-row-${c.company_id}`}
                    >
                      <TableCell className="py-2.5">
                        <p className="text-xs font-medium text-slate-200">{c.company_name}</p>
                        <p className="text-[10px] text-slate-500">{c.billing_email}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400">{c.plan_name}</Badge>
                      </TableCell>
                      <TableCell className="text-center text-xs text-slate-400">{c.user_count}</TableCell>
                      <TableCell className="text-center text-xs text-slate-400">{c.project_count}</TableCell>
                      <TableCell className="text-center text-xs text-slate-400">{c.unit_count}</TableCell>
                      <TableCell>
                        <Badge className={`text-[10px] ${c.is_active ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="w-3 h-3 text-slate-600" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {companies.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-xs text-slate-500 py-8">No companies found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        {/* Detail Panel */}
        {selected && (
          <div className="w-2/5 bg-slate-900 border border-slate-800 rounded-lg p-4 space-y-4 max-h-[75vh] overflow-y-auto" data-testid="company-detail-panel">
            {detailLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
            ) : detail ? (
              <>
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-slate-200">{detail.company.company_name}</h2>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-500 hover:text-slate-300" onClick={() => { setSelected(null); setDetail(null); }}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500">{detail.company.billing_email} | ID: {detail.company.company_id}</p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-800/50 rounded p-2.5 text-center">
                    <p className="text-base font-bold text-slate-200">{detail.stats.user_count}</p>
                    <p className="text-[10px] text-slate-500">Users</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-2.5 text-center">
                    <p className="text-base font-bold text-slate-200">{detail.stats.project_count}</p>
                    <p className="text-[10px] text-slate-500">Projects</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-2.5 text-center">
                    <p className="text-base font-bold text-slate-200">{detail.stats.unit_count}</p>
                    <p className="text-[10px] text-slate-500">Units</p>
                  </div>
                </div>

                {/* Plan */}
                {detail.plan && (
                  <div className="flex items-center justify-between p-2.5 bg-slate-800/50 rounded text-xs">
                    <span className="text-slate-400">Current Plan</span>
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">{detail.plan.name}</Badge>
                  </div>
                )}
                {detail.subscription && (
                  <div className="flex items-center justify-between p-2.5 bg-slate-800/50 rounded text-xs">
                    <span className="text-slate-400">Payment</span>
                    <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px]">{detail.subscription.payment_status || 'N/A'}</Badge>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs border-slate-700 text-slate-300 hover:bg-slate-800"
                    onClick={() => toggleStatus(selected, detail.company.is_active)}
                    data-testid="platform-toggle-company"
                  >
                    {detail.company.is_active ? <ToggleRight className="w-3.5 h-3.5 mr-1 text-emerald-400" /> : <ToggleLeft className="w-3.5 h-3.5 mr-1" />}
                    {detail.company.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white"
                    onClick={openAssignPlan}
                    data-testid="platform-assign-plan"
                  >
                    <Crown className="w-3.5 h-3.5 mr-1" /> Assign Plan
                  </Button>
                </div>

                {/* Users */}
                <div>
                  <p className="text-xs font-medium text-slate-300 mb-1.5">Users ({detail.users.length})</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {detail.users.map(u => (
                      <div key={u.user_id} className="flex items-center justify-between text-[10px] py-1.5 px-2 bg-slate-800/40 rounded">
                        <span className="text-slate-300">{u.name} ({u.email})</span>
                        <Badge variant="outline" className="text-[9px] border-slate-700 text-slate-500">{u.role}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Projects */}
                {detail.projects.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-300 mb-1.5">Projects ({detail.projects.length})</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {detail.projects.map(p => (
                        <div key={p.project_id} className="flex items-center justify-between text-[10px] py-1.5 px-2 bg-slate-800/40 rounded">
                          <span className="text-slate-300">{p.name}</span>
                          <span className="text-slate-500">{p.total_units} units</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Onboarding */}
                {detail.onboarding && (
                  <div>
                    <p className="text-xs font-medium text-slate-300 mb-1.5">Onboarding Data</p>
                    <pre className="text-[10px] text-slate-400 bg-slate-800/40 p-2.5 rounded overflow-auto max-h-32">
                      {JSON.stringify(detail.onboarding, null, 2)}
                    </pre>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Assign Plan Dialog */}
      <Dialog open={showAssignPlan} onOpenChange={setShowAssignPlan}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-200">Assign Plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Select Plan</Label>
              <Select value={assignPlanId} onValueChange={setAssignPlanId}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-300 h-8 text-xs" data-testid="platform-assign-plan-select">
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {plans.map(p => (
                    <SelectItem key={p.plan_id} value={p.plan_id} className="text-slate-300 text-xs">
                      {p.name} - Rs.{p.price_monthly}/mo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[10px] text-slate-500">Plan will be assigned as "waived" (no payment).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 h-7 text-xs" onClick={() => setShowAssignPlan(false)}>Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs" onClick={handleAssignPlan} disabled={!assignPlanId} data-testid="platform-confirm-assign-plan">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformCompanies;
